import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Building2, CheckCircle2, Sparkles, FileText, ArrowUpRight,
  TrendingUp, Landmark, ShieldCheck, Pencil,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/pages/Dashboard";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Stepper, StartStep, StateStep, EntityStep, BusinessInfoStep, OwnersStep,
  EinStep, LicensesStep, BankingStep, DocumentsStep, ReviewStep,
} from "@/pages/formation/FormationSteps";

function deepMerge(base, patch) {
  const out = { ...base };
  for (const k in patch) {
    if (patch[k] && typeof patch[k] === "object" && !Array.isArray(patch[k]) && typeof out[k] === "object" && !Array.isArray(out[k])) {
      out[k] = deepMerge(out[k], patch[k]);
    } else out[k] = patch[k];
  }
  return out;
}

const SLICE = {
  start: (p) => ({ starting_point: p.starting_point }),
  state: (p) => ({ state: p.state }),
  entity: (p) => ({ entity_type: p.entity_type }),
  "business-info": (p) => ({ business: p.business }),
  owners: (p) => ({ owners: p.owners }),
  ein: (p) => ({ ein: p.ein }),
  licenses: () => ({}),
  banking: (p) => ({ banking: p.banking }),
  documents: () => ({}),
  review: () => ({}),
};

function isValid(step, p) {
  const b = p.business || {};
  if (step === "start") return !!p.starting_point;
  if (step === "state") return !!p.state;
  if (step === "entity") return !!p.entity_type;
  if (step === "business-info") return !!b.name && !!b.industry;
  if (step === "owners") return (p.owners || []).length > 0 && p.owners.every((o) => o.name);
  if (step === "ein") return p.ein?.has_ein === true || p.ein?.has_ein === false;
  return true;
}

function CompletedView({ wf, profile, onEdit }) {
  const navigate = useNavigate();
  const b = profile.business || {};
  const entity = wf.entity_types.find((e) => e.id === profile.entity_type);
  const NEXT = [
    { title: "Build Business Credit", desc: "Get a D-U-N-S number and net-30 tradelines.", icon: TrendingUp, path: "/credit" },
    { title: "Explore Funding", desc: "Find loans, grants, and investors that fit.", icon: Landmark, path: "/funding" },
    { title: "Open Business Banking", desc: "Use your generated banking resolution.", icon: Building2, path: "/documents" },
    { title: "Stay Compliant", desc: "Track filings, licenses, and deadlines.", icon: ShieldCheck, path: "/compliance" },
  ];
  return (
    <div className="fv-fade-up max-w-4xl" data-testid="formation-completed">
      <PageHeader eyebrow="Build" title="Business Formation Center"
        subtitle="Your business formation is complete. Everything is saved to your Business Profile and shared across Fairview Systems." />
      <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6 mb-6 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl fv-gold-gradient flex items-center justify-center shrink-0"><CheckCircle2 className="w-6 h-6 text-[#0A0A0B]" /></div>
        <div className="flex-1">
          <div className="font-heading text-2xl text-white">{b.name}</div>
          <div className="text-sm text-[#A1A1AA]">{entity?.name} · {profile.state} · {b.industry}</div>
        </div>
        <button onClick={onEdit} data-testid="edit-formation-btn"
          className="flex items-center gap-1.5 text-sm h-10 px-4 rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-colors shrink-0">
          <Pencil className="w-4 h-4" /> Edit
        </button>
      </div>

      <div className="rounded-2xl border border-white/8 bg-[#121214] p-6 mb-6" data-testid="completed-profile">
        <h3 className="font-heading text-lg text-white mb-4">Business Profile</h3>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          {[["Legal name", b.name], ["DBA", b.dba || "—"], ["Entity", entity?.name], ["State", profile.state],
            ["Industry", b.industry], ["NAICS", b.naics || "—"], ["EIN", profile.ein?.ein_number || (profile.ein?.has_ein ? "On file" : "To obtain")],
            ["Owners", (profile.owners || []).map((o) => `${o.name} (${o.ownership_pct}%)`).join(", ") || "—"]].map(([k, v]) => (
            <div key={k}><div className="text-[11px] uppercase tracking-wider text-[#71717A]">{k}</div><div className="text-white">{v}</div></div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-white/8 bg-[#121214] p-6 mb-6" data-testid="completed-documents">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-white">Formation documents</h3>
          <button onClick={() => navigate("/documents")} data-testid="open-documents-btn" className="text-sm text-[#D4AF37] hover:underline flex items-center gap-1">Document Library <ArrowUpRight className="w-4 h-4" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {(profile.documents_generated || []).map((d) => (
            <div key={d} className="flex items-center gap-2 text-sm text-[#D4D4D8]"><FileText className="w-4 h-4 text-[#D4AF37] shrink-0" />{d}</div>
          ))}
        </div>
      </div>

      <h3 className="font-heading text-lg text-white mb-3">Recommended next modules</h3>
      <div className="grid sm:grid-cols-2 gap-3 mb-6" data-testid="completed-next">
        {NEXT.map((n) => (
          <button key={n.title} onClick={() => navigate(n.path)} data-testid={`next-${n.path.slice(1)}`}
            className="text-left rounded-xl border border-white/8 bg-[#121214] p-4 hover:border-[#D4AF37]/40 transition-colors flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center shrink-0"><n.icon className="w-4 h-4 text-[#D4AF37]" /></div>
            <div><div className="text-white text-sm font-medium">{n.title}</div><div className="text-xs text-[#71717A]">{n.desc}</div></div>
          </button>
        ))}
      </div>

      <button onClick={() => navigate("/ai-assistant")} data-testid="completed-ai-cta"
        className="w-full rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-5 flex items-center gap-4 hover:border-[#D4AF37]/40 transition-colors">
        <div className="w-11 h-11 rounded-xl fv-gold-gradient flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-[#0A0A0B]" /></div>
        <div className="flex-1 text-left"><div className="font-heading text-lg text-white">Ask your AI Advisor what's next</div><div className="text-sm text-[#A1A1AA]">It now knows your full business profile and can guide credit, funding, and compliance.</div></div>
        <ArrowUpRight className="w-5 h-5 text-[#D4AF37] shrink-0" />
      </button>
    </div>
  );
}

export default function BusinessFormation() {
  const [wf, setWf] = useState(null);
  const [profile, setProfile] = useState(null);
  const [step, setStep] = useState("start");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState([]);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/formation/workflow");
    setWf(data);
    setProfile(data.profile);
    setStep(data.profile.workflow.current_step || "start");
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (patch) => setProfile((p) => deepMerge(p, patch));

  const selectState = async (v) => {
    update({ state: v });
    try {
      const { data } = await api.put("/formation/workflow/step", { step: "state", data: { state: v }, advance: false });
      setWf((w) => ({ ...w, guidance: data.guidance, licenses: data.licenses, banking_checklist: data.banking_checklist, ai_review: data.ai_review }));
      setProfile(data.profile);
    } catch (e) { console.warn("State autosave deferred:", e); }
  };

  const persist = async (stepId, advance) => {
    const slice = SLICE[stepId](profile);
    const { data } = await api.put("/formation/workflow/step", { step: stepId, data: slice, advance });
    setWf((w) => ({ ...w, ...data }));
    setProfile(data.profile);
    return data;
  };

  const goNext = async () => {
    if (!isValid(step, profile)) { toast.error("Please complete the required fields."); return; }
    setSaving(true);
    try {
      await persist(step, true);
      const idx = wf.steps.findIndex((s) => s.id === step);
      setStep(wf.steps[Math.min(idx + 1, wf.steps.length - 1)].id);
      window.scrollTo(0, 0);
    } catch { toast.error("Could not save. Please try again."); }
    finally { setSaving(false); }
  };

  const goBack = async () => {
    setSaving(true);
    try { await persist(step, false); } catch (e) { console.warn("Formation autosave deferred:", e); }
    setSaving(false);
    const idx = wf.steps.findIndex((s) => s.id === step);
    setStep(wf.steps[Math.max(idx - 1, 0)].id);
    window.scrollTo(0, 0);
  };

  const jump = async (target) => {
    setSaving(true);
    try { await persist(step, false); } catch (e) { console.warn("Formation autosave deferred:", e); }
    setSaving(false);
    setStep(target); window.scrollTo(0, 0);
  };

  const generateDocs = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/formation/workflow/generate-documents");
      setGenerated(data.documents);
      setProfile((p) => ({ ...p, documents_generated: data.documents.map((d) => d.section) }));
      toast.success(`${data.documents.length} documents saved to your Document Library`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate documents"); }
    finally { setGenerating(false); }
  };

  const complete = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/formation/workflow/complete");
      setProfile(data.profile);
      setWf((w) => ({ ...w, ai_review: data.ai_review }));
      setEditing(false);
      toast.success("Business formation complete! 🎉");
      window.scrollTo(0, 0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Please complete required steps first"); }
    finally { setSaving(false); }
  };

  if (!wf || !profile) return <p className="text-[#71717A]">Loading…</p>;

  if (profile.workflow.status === "completed" && !editing) {
    return <CompletedView wf={wf} profile={profile} onEdit={() => { setEditing(true); setStep("start"); }} />;
  }

  const completed = profile.workflow.completed_steps || [];
  const meta = wf.steps.find((s) => s.id === step);
  const idx = wf.steps.findIndex((s) => s.id === step);
  const pct = Math.round((completed.length / wf.steps.length) * 100);

  const stepProps = { profile, update, wf };
  const STEP_UI = {
    start: <StartStep {...stepProps} />,
    state: <StateStep profile={profile} wf={wf} onStateChange={selectState} />,
    entity: <EntityStep {...stepProps} />,
    "business-info": <BusinessInfoStep {...stepProps} />,
    owners: <OwnersStep {...stepProps} />,
    ein: <EinStep {...stepProps} />,
    licenses: <LicensesStep {...stepProps} />,
    banking: <BankingStep {...stepProps} />,
    documents: <DocumentsStep profile={profile} onGenerate={generateDocs} generating={generating} generated={generated} />,
    review: <ReviewStep review={wf.ai_review} onComplete={complete} completing={saving} completed={profile.workflow.status === "completed"} />,
  };

  return (
    <div className="fv-fade-up max-w-4xl" data-testid="formation-wizard">
      <PageHeader eyebrow="Build" title="Business Formation Center"
        subtitle="A guided, step-by-step application to legally form and organize your business. Your progress saves automatically." />

      <div className="rounded-2xl border border-white/8 bg-[#121214] p-4 mb-6">
        <div className="flex justify-between text-xs mb-2"><span className="text-[#A1A1AA]">Overall progress</span><span className="text-[#D4AF37]" data-testid="formation-overall-pct">{pct}%</span></div>
        <Progress value={pct} className="h-2 bg-white/5" />
      </div>

      <Stepper steps={wf.steps} current={step} completed={completed} onJump={jump} />

      <div className="mb-2">
        <div className="text-[11px] uppercase tracking-wider text-[#71717A]">Step {idx + 1} of {wf.steps.length}</div>
        <h2 className="font-heading text-2xl text-white">{meta.title}</h2>
        <p className="text-sm text-[#71717A]">{meta.desc}</p>
      </div>

      <div className="mt-5 pb-24" data-testid="formation-step-content">{STEP_UI[step]}</div>

      <div className="flex items-center justify-between gap-3 pt-6 border-t border-white/8 sticky bottom-0 bg-[#0A0A0B]/80 backdrop-blur pb-2">
        <button onClick={goBack} disabled={idx === 0 || saving} data-testid="formation-back-btn"
          className="flex items-center gap-1.5 text-sm h-11 px-5 rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {step !== "review" && (
          <button onClick={goNext} disabled={saving || !isValid(step, profile)} data-testid="formation-next-btn"
            className="flex items-center gap-1.5 fv-gold-gradient text-[#0A0A0B] rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? "Saving…" : "Continue"} <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
